import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ExamType, ExamSummary, MyExamAttempt } from '@proref/types';
import { EXAM_PASS_SCORE } from '@proref/schemas';
import { PageHeader } from '../components/shared/PageHeader';
import { Icon } from '../components/shared/Icons';
import { Button } from '../components/shared/Button';
import { Input } from '../components/shared/Input';
import { Carousel } from '../components/shared/Carousel';
import { Spinner } from '../components/shared/Spinner';
import { ListSkeleton } from '../components/shared/Skeleton';
import { apiErrorMessage } from '../lib/api';
import { notifyError, notifySuccess } from '../lib/toast';
import {
  useExamsQuery,
  useMyExamAttemptsQuery,
  useStartExamAttempt,
  useAbandonExamAttempt,
} from '../hooks/use-exams';
import { ExamRunnerDialog } from './ExamRunnerPage';
import { StartPracticeTestDialog } from '../components/exams/StartPracticeTestDialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

// The "N questions · M min" pill cycles through soft pastel tints by card index, so the pack grid
// reads as a colourful set (matching the design). Safe as fixed Tailwind colours — v1 bakes a
// single light palette (see tokens.css), so there's no dark theme for these to clash with.
const PILL_TINTS = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-teal-100 text-teal-700',
  'bg-violet-100 text-violet-700',
  'bg-sky-100 text-sky-700',
  'bg-emerald-100 text-emerald-700',
];

function pct(score: number | null): number {
  return score === null ? 0 : Math.round(score * 100);
}

// ≥70 pass (accent), 50–69 muted, <50 danger — the shared meaning behind the result colours.
function scoreTone(p: number): string {
  if (p >= 70) return 'text-accent';
  if (p >= 50) return 'text-ink-2';
  return 'text-danger';
}
function barTone(p: number): string {
  if (p >= 70) return 'bg-accent';
  if (p >= 50) return 'bg-ink-3';
  return 'bg-danger';
}

// Plain functions, not components — they take `t` as a parameter rather than calling
// useTranslation() themselves (only components/hooks may do that); every call site already has
// it from its own render.
function minutesLabel(t: TFunction, sec: number | null): string {
  return sec === null ? t('exams.landing.untimed') : t('exams.landing.minutesLabel', { minutes: Math.round(sec / 60) });
}

// Coarse "time ago" for the resume banner (started 11m ago).
function timeAgo(t: TFunction, iso: string): string {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return t('exams.landing.justNow');
  const m = Math.floor(s / 60);
  if (m < 60) return t('exams.landing.minutesAgo', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('exams.landing.hoursAgo', { count: h });
  return t('exams.landing.daysAgo', { count: Math.floor(h / 24) });
}

function mmss(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function dayLabel(t: TFunction, iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(today) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return t('exams.landing.today');
  if (diffDays === 1) return t('exams.landing.yesterday');
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

// A small SVG progress ring (answered / total) for the resume banner.
function ProgressRing({ done, total }: { done: number; total: number }) {
  const frac = total > 0 ? done / total : 0;
  const r = 20;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 48 48" className="h-12 w-12 flex-none -rotate-90">
      <circle cx="24" cy="24" r={r} fill="none" stroke="currentColor" strokeWidth="4" className="text-line-2" />
      <circle
        cx="24"
        cy="24"
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - frac)}
        className="text-accent"
      />
    </svg>
  );
}

function ResumeBanner({ attempt, onResume }: { attempt: MyExamAttempt; onResume: (attemptId: string) => void }) {
  const { t } = useTranslation();
  const abandon = useAbandonExamAttempt();
  // Discarding an attempt is destructive (its saved answers go with it) → confirm first.
  const [confirmOpen, setConfirmOpen] = useState(false);
  // time left = started_at + limit − now (null/untimed → no countdown shown).
  const deadlineMs = attempt.time_limit_sec === null ? null : new Date(attempt.started_at).getTime() + attempt.time_limit_sec * 1000;
  const secLeft = deadlineMs === null ? null : (deadlineMs - Date.now()) / 1000;

  async function doAbandon() {
    try {
      await abandon.mutateAsync(attempt.id);
      setConfirmOpen(false);
      notifySuccess(t('exams.landing.resume.abandonedToast'));
    } catch (err) {
      notifyError(err, t('exams.landing.resume.abandonErrorToast'));
    }
  }

  return (
    <div className="flex items-center gap-4 rounded-lg border border-accent/40 bg-accent/5 px-5 py-4">
      <div className="text-accent">
        <ProgressRing done={attempt.answered_count} total={attempt.question_count} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
          {t('exams.landing.resume.eyebrow', { time: timeAgo(t, attempt.started_at) })}
        </div>
        <div className="mt-0.5 truncate text-[15px] font-medium text-ink">{attempt.exam_title}</div>
        <div className="mt-0.5 text-[12.5px] text-muted">
          {t('exams.landing.resume.questionOf', {
            current: Math.min(attempt.answered_count + 1, attempt.question_count),
            total: attempt.question_count,
          })}
          {secLeft !== null && secLeft > 0 && t('exams.landing.resume.timeLeft', { time: mmss(secLeft) })}
          {secLeft !== null && secLeft <= 0 && t('exams.landing.resume.timeUp')}
        </div>
      </div>
      <div className="flex flex-none items-center gap-3">
        <button
          type="button"
          className="text-[12.5px] text-muted hover:text-ink disabled:opacity-50"
          onClick={() => setConfirmOpen(true)}
          disabled={abandon.isPending}
          title={t('exams.landing.resume.confirmTitle')}
        >
          {t('exams.landing.resume.abandonLink')}
        </button>
        <Button variant="accent" onClick={() => onResume(attempt.id)}>
          {t('exams.landing.resume.resumeButton')} <Icon.arrow className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Confirm before discarding — abandoning deletes the attempt and its saved answers. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('exams.landing.resume.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('exams.landing.resume.confirmBody', {
                title: attempt.exam_title,
                answered: attempt.answered_count,
                total: attempt.question_count,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={abandon.isPending}>{t('exams.landing.resume.keepAttempt')}</AlertDialogCancel>
            {/* Not AlertDialogAction — that auto-closes on click; we close only after the request
                resolves (in doAbandon) so a failure keeps the dialog open with the error toast. */}
            <Button variant="danger" onClick={doAbandon} disabled={abandon.isPending}>
              {abandon.isPending ? <Spinner className="h-4 w-4" /> : t('exams.landing.resume.abandonAttempt')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExamPackCard({
  exam,
  index,
  attempts,
  onStarted,
}: {
  exam: ExamSummary;
  index: number;
  attempts: MyExamAttempt[];
  onStarted: (attemptId: string) => void;
}) {
  const { t } = useTranslation();
  const start = useStartExamAttempt();

  // A platform (federation-less) test is an "Official" ProRef test; else it's a federation add-on.
  const isOfficial = exam.federation_id === null;
  // An Official (proctored) exam — needs the admin's access password to start (distinct concept
  // from the platform badge above; here it just means "password-gated + auto-timed").
  const proctored = exam.is_proctored;
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState('');

  // This referee's history for this exam — attempt count + most recent completed score.
  const mine = attempts.filter((a) => a.exam_id === exam.id);
  const lastCompleted = mine.find((a) => a.completed_at !== null);

  async function startTest(password?: string) {
    try {
      const state = await start.mutateAsync({ examId: exam.id, password });
      setPwOpen(false);
      setPw('');
      // Hand the fresh attempt to the landing, which opens it in the focused runner dialog.
      onStarted(state.attempt.id);
    } catch (err) {
      // A wrong password is a 403 — keep the dialog open so they can retry.
      notifyError(err, t('exams.landing.packCard.startErrorToast'));
    }
  }

  // Proctored exams gate on a password; everything else starts immediately.
  function onStartClick() {
    if (proctored) setPwOpen(true);
    else void startTest();
  }

  return (
    // h-full + a bottom-pinned button (mt-auto) so every card in a row is the same height, and the
    // title reserves two lines so a 1-line and a 2-line title don't produce different heights.
    <div className="flex h-full flex-col rounded-lg border border-line bg-surface p-4 shadow-1">
      <div className="flex items-start justify-between gap-2">
        <span className={'rounded-full px-2.5 py-0.5 font-mono text-[11px] ' + PILL_TINTS[index % PILL_TINTS.length]}>
          {t('exams.landing.packCard.questionsAndTime', {
            count: exam.question_count,
            time: minutesLabel(t, exam.time_limit_sec),
          })}
        </span>
        <div className="flex flex-none items-center gap-1.5">
          {/* Per-card badge so the provenance stays clear even out of its group (e.g. mixed scroll). */}
          {isOfficial && (
            <span className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2 py-0.5 text-[10.5px] font-semibold text-accent">
              <Icon.spark className="h-3 w-3" /> {t('exams.landing.official')}
            </span>
          )}
          {/* Proctored = password-gated. A lock chip (not the word "Official", which the badge above
              already uses for platform provenance) signals the referee will need a password. */}
          {proctored && (
            <span className="inline-flex items-center gap-1 rounded-full bg-ink/10 px-2 py-0.5 text-[10.5px] font-semibold text-ink-2">
              <Icon.lock className="h-3 w-3" /> {t('exams.landing.packCard.password')}
            </span>
          )}
          {mine.length > 0 && <span className="font-mono text-[11px] text-muted">×{mine.length}</span>}
        </div>
      </div>
      <h3 className="mt-3 line-clamp-2 min-h-[2.6rem] text-[15px] font-semibold leading-[1.3] text-ink">{exam.title}</h3>
      {/* The last score opens that sitting's review. It used to be plain text, which made an
          attempted pack a dead end: the figure was shown with no way back to the questions. */}
      {lastCompleted ? (
        <Link
          to={`/exam-attempts/${lastCompleted.id}/results`}
          className="mt-1 inline-flex w-fit items-center gap-1 text-[13px] text-muted hover:text-ink"
        >
          {t('exams.landing.packCard.lastScore')}{' '}
          <span className={'font-semibold ' + scoreTone(pct(lastCompleted.score))}>{pct(lastCompleted.score)}%</span>
          <Icon.chevronRight className="h-3 w-3" />
        </Link>
      ) : (
        <p className="mt-1 text-[13px] text-muted">{t('exams.landing.notAttempted')}</p>
      )}
      <Button variant="primary" block className="mt-4" onClick={onStartClick} disabled={start.isPending}>
        {start.isPending ? <Spinner className="w-4 h-4" /> : (
          <>
            {t('exams.landing.packCard.startTest')} <Icon.arrow className="h-3.5 w-3.5" />
          </>
        )}
      </Button>

      {/* Official (proctored) exams prompt for the admin's access password before starting. */}
      {proctored && (
        <AlertDialog
          open={pwOpen}
          onOpenChange={(o) => {
            setPwOpen(o);
            if (!o) setPw('');
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('exams.landing.packCard.passwordDialogTitle')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('exams.landing.packCard.passwordDialogBody', {
                  title: exam.title,
                  time: minutesLabel(t, exam.time_limit_sec),
                })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            {/* A form so Enter submits; we close only after the request resolves (wrong password
                stays open with the error toast). */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void startTest(pw);
              }}
            >
              <Input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder={t('exams.landing.packCard.passwordPlaceholder')}
                autoFocus
              />
              <AlertDialogFooter className="mt-3">
                <AlertDialogCancel type="button" disabled={start.isPending}>
                  {t('exams.landing.cancel')}
                </AlertDialogCancel>
                <Button type="submit" variant="accent" disabled={start.isPending || pw.length === 0}>
                  {start.isPending ? <Spinner className="w-4 h-4" /> : t('exams.landing.packCard.startExam')}
                </Button>
              </AlertDialogFooter>
            </form>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

// Career summary across every completed sitting — how many exams, how many cleared the pass
// mark, and the average. Sits above the recent-results list because it answers the question a
// referee actually opens this page with ("where do I stand?") before the per-attempt detail.
function HistoryStats({ attempts }: { attempts: MyExamAttempt[] }) {
  const { t } = useTranslation();
  const completed = attempts.filter((a) => a.completed_at !== null);
  if (completed.length === 0) return null;

  const passed = completed.filter((a) => (a.score ?? 0) >= EXAM_PASS_SCORE).length;
  const average = completed.reduce((sum, a) => sum + (a.score ?? 0), 0) / completed.length;

  const stats = [
    { label: t('exams.landing.history.examsTaken'), value: String(completed.length), tone: 'text-ink' },
    { label: t('exams.landing.history.passed'), value: `${passed}/${completed.length}`, tone: passed > 0 ? 'text-accent' : 'text-ink-2' },
    { label: t('exams.landing.history.average'), value: `${pct(average)}%`, tone: scoreTone(pct(average)) },
  ];

  return (
    <div className="grid grid-cols-3 gap-3">
      {stats.map((stat) => (
        <div key={stat.label} className="rounded-lg border border-line bg-surface p-4">
          <div className={'font-display text-[26px] leading-none ' + stat.tone}>{stat.value}</div>
          <div className="mt-1.5 text-[11.5px] text-muted">{stat.label}</div>
        </div>
      ))}
    </div>
  );
}

// Remove one finished practice sitting from your own history. Absent on an Official exam — that
// result is the federation's record, and the API refuses it there too (this only hides a button
// the server would reject).
function DeleteAttemptButton({ attempt }: { attempt: MyExamAttempt }) {
  const { t } = useTranslation();
  const del = useAbandonExamAttempt();

  async function doDelete() {
    try {
      await del.mutateAsync(attempt.id);
      notifySuccess(t('exams.landing.deleteAttempt.removedToast'));
    } catch (err) {
      notifyError(err, t('exams.landing.deleteAttempt.removeErrorToast'));
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          icon
          aria-label={t('exams.landing.deleteAttempt.removeAria', { examTitle: attempt.exam_title })}
        >
          <Icon.trash className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('exams.landing.deleteAttempt.confirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('exams.landing.deleteAttempt.confirmBody', { title: attempt.exam_title })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={del.isPending}>{t('exams.landing.deleteAttempt.keepIt')}</AlertDialogCancel>
          <Button variant="danger" onClick={doDelete} disabled={del.isPending}>
            {del.isPending ? <Spinner className="h-4 w-4" /> : t('exams.landing.deleteAttempt.removeAttempt')}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RecentResults({ attempts }: { attempts: MyExamAttempt[] }) {
  const { t } = useTranslation();
  // Completed attempts, newest first, last 5. Delta = vs the previous completed attempt of the same exam.
  const completed = attempts.filter((a) => a.completed_at !== null);
  const rows = completed.slice(0, 5).map((a, i) => {
    const prev = completed.slice(i + 1).find((p) => p.exam_id === a.exam_id);
    const delta = prev ? pct(a.score) - pct(prev.score) : null;
    return { a, delta };
  });

  if (rows.length === 0) return null;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <h2 className="text-[15px] font-semibold text-ink">{t('exams.landing.recentResults.heading')}</h2>
      <p className="text-[12.5px] text-muted">{t('exams.landing.recentResults.subtitle', { count: rows.length })}</p>
      <div className="mt-4 flex flex-col">
        {rows.map(({ a, delta }) => {
          const p = pct(a.score);
          return (
            <div
              key={a.id}
              className="group flex items-center gap-4 border-b border-line last:border-0 hover:bg-surface-2"
            >
              {/* The row opens the sitting's review. The delete control is a SIBLING of the link
                  rather than inside it — a button nested in a link is ambiguous to keyboard and
                  screen-reader users, and swallowing its click with stopPropagation only papers
                  over that. */}
              <Link
                to={`/exam-attempts/${a.id}/results`}
                className="flex min-w-0 flex-1 items-center gap-4 py-2.5"
                aria-label={t('exams.landing.recentResults.reviewAria', { examTitle: a.exam_title, pct: p })}
              >
                <span className="w-16 flex-none font-mono text-[11px] uppercase text-muted">{dayLabel(t, a.completed_at!)}</span>
                <span className="min-w-0 flex-1 truncate text-[13px] text-ink group-hover:underline">{a.exam_title}</span>
                <div className="hidden h-1.5 w-40 flex-none overflow-hidden rounded-full bg-surface-2 sm:block">
                  <div className={'h-full rounded-full ' + barTone(p)} style={{ width: `${p}%` }} />
                </div>
                <span className={'w-12 flex-none text-right text-[13px] font-semibold ' + scoreTone(p)}>{p}%</span>
                <span className="w-8 flex-none text-right font-mono text-[11px] text-muted">
                  {delta === null ? '–' : delta > 0 ? `+${delta}` : delta < 0 ? `${delta}` : '–'}
                </span>
              </Link>
              <span className="w-8 flex-none text-right">{!a.is_proctored && <DeleteAttemptButton attempt={a} />}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A labelled block of exam-pack cards — "Official" (ProRef platform tests) or "Your federation"
// (the federation's own add-ons). Rendered only when its group is non-empty.
function ExamGroup({
  variant,
  exams,
  attempts,
  onStarted,
}: {
  variant: 'official' | 'federation';
  exams: ExamSummary[];
  attempts: MyExamAttempt[];
  onStarted: (attemptId: string) => void;
}) {
  const { t } = useTranslation();
  const meta =
    variant === 'official'
      ? { icon: Icon.spark, label: t('exams.landing.official'), sub: t('exams.landing.group.officialSub') }
      : { icon: Icon.building, label: t('exams.landing.yourFederation'), sub: t('exams.landing.group.federationSub') };
  const Glyph = meta.icon;

  return (
    <section>
      <div className="mb-3 flex items-center gap-2">
        <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-accent/15 text-accent">
          <Glyph className="h-3.5 w-3.5" />
        </span>
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-ink">{meta.label}</h3>
          <p className="text-[11.5px] text-muted">{meta.sub}</p>
        </div>
        <span className="ml-auto font-mono text-[11px] text-muted">{exams.length}</span>
      </div>
      {/* Button-driven horizontal carousel (not auto-scrolling). The track is a single row of cards
          that slides horizontally; `w-max` lets it grow past the viewport, and each card's `h-full`
          + `items-stretch` (flex default) keeps every card in the row the same height. */}
      <Carousel ariaLabel={t('exams.landing.group.carouselAria', { label: meta.label })}>
        <div className="flex w-max items-stretch gap-4">
          {exams.map((exam, i) => (
            <div key={exam.id} className="w-[280px] flex-none">
              <ExamPackCard exam={exam} index={i} attempts={attempts} onStarted={onStarted} />
            </div>
          ))}
        </div>
      </Carousel>
    </section>
  );
}

// ─── Filtering ─────────────────────────────────────────────────────────────────

type ScopeFilter = 'all' | 'official' | 'federation';
type StatusFilter = 'all' | 'todo' | 'done';

// A packed exam is "done" for the referee once they have at least one completed attempt of it
// (which is exactly when the card shows a Last score rather than "Not attempted").
function isAttempted(examId: string, attempts: MyExamAttempt[]): boolean {
  return attempts.some((a) => a.exam_id === examId && a.completed_at !== null);
}

// A compact filter popover for the exam-pack rows. Source is only offered when both kinds of test
// are present (otherwise it's a no-op choice). Closes on outside-click or Escape.
function PackFilter({
  scope,
  setScope,
  status,
  setStatus,
  showScope,
  activeCount,
  onClear,
}: {
  scope: ScopeFilter;
  setScope: (v: ScopeFilter) => void;
  status: StatusFilter;
  setStatus: (v: StatusFilter) => void;
  showScope: boolean;
  activeCount: number;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <Button variant="default" size="sm" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="menu">
        <Icon.filter className="h-3.5 w-3.5" /> {t('exams.landing.filter.filter')}
        {activeCount > 0 && (
          <span className="ml-0.5 grid h-4 min-w-[16px] place-items-center rounded-full bg-accent px-1 text-[10px] font-semibold text-accent-ink">
            {activeCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 z-20 mt-1.5 w-56 rounded-lg border border-line bg-surface p-3 shadow-pop">
          {showScope && (
            <FilterSection label={t('exams.landing.filter.source')}>
              <FilterOptions
                value={scope}
                onChange={setScope}
                options={[
                  ['all', t('exams.landing.filter.allSources')],
                  ['official', t('exams.landing.official')],
                  ['federation', t('exams.landing.yourFederation')],
                ]}
              />
            </FilterSection>
          )}
          <FilterSection label={t('exams.landing.filter.status')}>
            <FilterOptions
              value={status}
              onChange={setStatus}
              options={[
                ['all', t('exams.landing.filter.all')],
                ['todo', t('exams.landing.notAttempted')],
                ['done', t('exams.landing.filter.attempted')],
              ]}
            />
          </FilterSection>
          {activeCount > 0 && (
            <button type="button" className="mt-1 px-1 text-[12px] text-muted hover:text-ink" onClick={onClear}>
              {t('exams.landing.filter.clearFilters')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FilterSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="mb-1 px-1 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">{label}</div>
      {children}
    </div>
  );
}

function FilterOptions<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {options.map(([v, label]) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={
            'flex items-center justify-between rounded-md px-2.5 py-1.5 text-left text-[12.5px] ' +
            (value === v ? 'bg-accent-tint font-medium text-accent' : 'text-ink-2 hover:bg-surface-2')
          }
        >
          {label}
          {value === v && <Icon.check className="h-3.5 w-3.5" />}
        </button>
      ))}
    </div>
  );
}

export interface ExamLandingProps {
  examType: ExamType;
  title: string;
  subtitle: string;
}

// Shared referee landing for both exam kinds (Rule tests / Video clip exams) — the layout from
// the design: resume banner, exam-pack grid, recent results.
export function ExamLanding({ examType, title, subtitle }: ExamLandingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const packsRef = useRef<HTMLDivElement>(null);
  const examsQuery = useExamsQuery({});
  const attemptsQuery = useMyExamAttemptsQuery();
  const attempts = useMemo(() => attemptsQuery.data ?? [], [attemptsQuery.data]);

  // The attempt currently open in the focused runner dialog (start a test / resume opens it here).
  const [runningAttemptId, setRunningAttemptId] = useState<string | null>(null);
  // The "Start a new test" length/timing dialog. Rule tests only — a clip exam draws from the clip
  // library, not the rule-question bank, so there's nothing to generate one from.
  const [practiceOpen, setPracticeOpen] = useState(false);
  const canGenerate = examType === 'rule_test';

  // Only this kind's published exams.
  const packs = useMemo(
    () => (examsQuery.data ?? []).filter((e) => e.type === examType && e.published_at !== null),
    [examsQuery.data, examType],
  );

  const kindAttempts = useMemo(() => attempts.filter((a) => a.exam_type === examType), [attempts, examType]);

  // Filters (Source + Status). Source is only meaningful when both platform and federation tests
  // exist, so we hide it otherwise.
  const [scope, setScope] = useState<ScopeFilter>('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const hasOfficial = useMemo(() => packs.some((e) => e.federation_id === null), [packs]);
  const hasFederation = useMemo(() => packs.some((e) => e.federation_id !== null), [packs]);
  const showScope = hasOfficial && hasFederation;
  const activeCount = (scope !== 'all' ? 1 : 0) + (status !== 'all' ? 1 : 0);

  // Apply the filters, then split the survivors by provenance so a referee can still tell an
  // Official (ProRef) test from their own federation's.
  const filteredPacks = useMemo(
    () =>
      packs.filter((e) => {
        if (scope === 'official' && e.federation_id !== null) return false;
        if (scope === 'federation' && e.federation_id === null) return false;
        const done = isAttempted(e.id, kindAttempts);
        if (status === 'todo' && done) return false;
        if (status === 'done' && !done) return false;
        return true;
      }),
    [packs, scope, status, kindAttempts],
  );
  const officialPacks = useMemo(() => filteredPacks.filter((e) => e.federation_id === null), [filteredPacks]);
  const federationPacks = useMemo(() => filteredPacks.filter((e) => e.federation_id !== null), [filteredPacks]);

  // The most recent in-progress attempt of this kind (drives the resume banner).
  const resume = useMemo(
    () => attempts.find((a) => a.completed_at === null && a.exam_type === examType),
    [attempts, examType],
  );

  return (
    <>
      <PageHeader
        title={title}
        sub={subtitle}
        actions={
          <Button
            variant="primary"
            onClick={() =>
              canGenerate ? setPracticeOpen(true) : packsRef.current?.scrollIntoView({ behavior: 'smooth' })
            }
          >
            {t('exams.landing.startNewTest')} <Icon.arrow className="h-3.5 w-3.5" />
          </Button>
        }
      />

      {canGenerate && (
        <StartPracticeTestDialog
          open={practiceOpen}
          onOpenChange={setPracticeOpen}
          // The generated test opens straight in the same focused runner an authored pack uses.
          onStarted={setRunningAttemptId}
        />
      )}

      {resume && (
        <div className="mb-6">
          <ResumeBanner attempt={resume} onResume={setRunningAttemptId} />
        </div>
      )}

      <div ref={packsRef} className="rounded-lg border border-line bg-bg-2 p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-ink">{t('exams.landing.examPacks')}</h2>
            <p className="text-[12.5px] text-muted">
              {examType === 'rule_test' ? t('exams.landing.ruleTestSubtitle') : t('exams.landing.clipExamSubtitle')}
            </p>
          </div>
          {/* Filter only matters once there's something to filter. */}
          {packs.length > 0 && (
            <PackFilter
              scope={scope}
              setScope={setScope}
              status={status}
              setStatus={setStatus}
              showScope={showScope}
              activeCount={activeCount}
              onClear={() => {
                setScope('all');
                setStatus('all');
              }}
            />
          )}
        </div>

        <div className="mt-4">
          {examsQuery.isLoading ? (
            <ListSkeleton rows={4} />
          ) : examsQuery.isError && !examsQuery.data ? (
            <div className="rounded border border-danger/40 bg-danger/10 px-4 py-3 text-[13px] text-danger">
              {apiErrorMessage(examsQuery.error, t('exams.landing.couldNotLoad'))}
            </div>
          ) : packs.length === 0 ? (
            <div className="grid place-items-center rounded border border-dashed border-line-2 bg-surface px-6 py-12 text-center">
              <p className="text-sm text-muted">{t('exams.landing.noExams')}</p>
            </div>
          ) : filteredPacks.length === 0 ? (
            // Data exists but the active filters exclude everything.
            <div className="grid place-items-center rounded border border-dashed border-line-2 bg-surface px-6 py-12 text-center">
              <p className="text-sm text-muted">{t('exams.landing.noMatch')}</p>
              <button
                type="button"
                className="mt-2 text-[12.5px] font-medium text-accent hover:underline"
                onClick={() => {
                  setScope('all');
                  setStatus('all');
                }}
              >
                {t('exams.landing.filter.clearFilters')}
              </button>
            </div>
          ) : (
            // Two groups (Official first), each rendered only when non-empty. When just one source
            // has tests the layout collapses to a single labelled block — no empty headers.
            <div className="flex flex-col gap-6">
              {officialPacks.length > 0 && (
                <ExamGroup variant="official" exams={officialPacks} attempts={kindAttempts} onStarted={setRunningAttemptId} />
              )}
              {federationPacks.length > 0 && (
                <ExamGroup variant="federation" exams={federationPacks} attempts={kindAttempts} onStarted={setRunningAttemptId} />
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <HistoryStats attempts={kindAttempts} />
        <RecentResults attempts={kindAttempts} />
      </div>

      {/* The test runs here — a focused shadcn Dialog over the landing. Closing without submitting
          keeps the autosaved progress; we refetch attempts so the Resume banner reappears. On
          submit the runner hands back and we route to the results review. */}
      <ExamRunnerDialog
        attemptId={runningAttemptId ?? undefined}
        open={runningAttemptId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRunningAttemptId(null);
            void attemptsQuery.refetch();
          }
        }}
        onSubmitted={() => {
          const id = runningAttemptId;
          setRunningAttemptId(null);
          if (id) navigate(`/exam-attempts/${id}/results`);
        }}
      />
    </>
  );
}
